import { ICompleteAmendBookingRequestData } from '../../../../../shared/api/gateway/types/ICompleteAmendBookingRequestData';
import { toLocalDateTime } from '../../../../../shared/i18n/date-time/LocalDateTime';
import { IBooking } from '../../models/IBooking';
import { NewBooking } from '../../models/NewBooking';
import { IParkingProduct } from '../../../parking-product/models/IParkingProduct';
import { getTimeIn24HourFormat } from '../../../utils/getTimeIn24HourFormat';
import { IExtra } from '../../../ancillary/domain/models/IExtra';
import { IExtraVariant } from '../../../ancillary/domain/models/IExtraVariant';

export function adaptBookingToCompleteAmendBookingRequestData(
	booking: NewBooking,
	originalBooking: IBooking,
	availableParkingProducts: IParkingProduct[],
	parkingProductsQuoteKey?: string,
): ICompleteAmendBookingRequestData | undefined {
	const {
		entryDate,
		entryTime,
		exitDate,
		exitTime,
		promoCode,
		parkingProduct,
		personalDetails,
		emailOptIn,
		vehicleDetails,
		travelDetails,
		paymentDetails,
		ancillaries,
	} = booking;

	if (
		!entryDate ||
		!exitDate ||
		!parkingProduct ||
		!personalDetails ||
		!vehicleDetails
	) {
		return undefined;
	}

	const selectedParkingProduct = availableParkingProducts.find(
		({ id }) => id === parkingProduct.id,
	);

	const selectedParkingProductKey =
		selectedParkingProduct?.key ?? parkingProductsQuoteKey;

	if (!selectedParkingProductKey || !selectedParkingProduct) {
		return undefined;
	}

	const totalPrice = booking.totalPrice.decimalValue;
	const parkingProductPrice =
		selectedParkingProduct.totalPrice ??
		selectedParkingProduct.price.value.decimalValue;
	const priceDifference = selectedParkingProduct?.priceDifference ?? 0;
	const priceDifferenceType = selectedParkingProduct?.priceDifferenceType;
	const hasPriceDifference =
		!!priceDifferenceType && Math.abs(priceDifference) > 0;
	const shouldSendPaymentDetails =
		priceDifferenceType === 'Charge' && priceDifference > 0;
	const amendPaymentTotal = Math.abs(priceDifference).toFixed(2);

	if (shouldSendPaymentDetails && !paymentDetails) {
		return undefined;
	}

	return {
		reference: originalBooking.reference,
		parkingProductId: parkingProduct.id,
		parkingProductKey: selectedParkingProductKey,
		timeWindow: {
			startDate: toLocalDateTime(entryDate).date,
			startTime: entryTime ?? getTimeIn24HourFormat(entryDate),
			endDate: toLocalDateTime(exitDate).date,
			endTime: exitTime ?? getTimeIn24HourFormat(exitDate),
		},
		languageCode: booking.locale,
		promoCode,
		personalDetails: {
			firstName: personalDetails.firstName,
			lastName: personalDetails.lastName,
			email: personalDetails.email,
			phoneNumber: personalDetails.phoneNumber,
			zipCode: personalDetails.zipCode,
			travelReason: travelDetails?.reasonForTravel,
		},
		vehicle: {
			licensePlateNumber: vehicleDetails.licensePlateNumber,
		},
		paymentDetails:
			shouldSendPaymentDetails && paymentDetails
				? {
						token: paymentDetails.token,
						tokenType: paymentDetails.tokenType,
						total: amendPaymentTotal,
				  }
				: undefined,
		ancillaries: adaptAmendAncillaries(ancillaries),
		optInMarketing: emailOptIn,
		parkingProductPrice,
		totalPrice,
		priceDifference: hasPriceDifference ? priceDifference : undefined,
		priceDifferenceType: hasPriceDifference ? priceDifferenceType : undefined,
	};
}

function adaptAmendAncillaries(selectedAncillaries: NewBooking['ancillaries']) {
	return selectedAncillaries.map((ancillary) => {
		const extras = ancillary.extras
			?.filter((extra) => extra.variants.length)
			.map(adaptExtra);

		return {
			id: ancillary.id,
			quantity: 1,
			price: ancillary.price?.toString(),
			extras: extras && extras.length ? extras : undefined,
		};
	});
}

function adaptExtra(extra: IExtra) {
	return {
		id: extra.id,
		variants: extra.variants.map(adaptVariant),
	};
}

function adaptVariant(variant: IExtraVariant) {
	return {
		id: variant.id,
		quantity: 1,
		price: variant.price.toString(),
	};
}
